/**
 * Tile - Represents a single tile in the world
 */

import { TileType, ResourceType } from '../types/enums';

export interface TileConfig {
  type: TileType;
  walkable: boolean;
  buildable: boolean;
  resourceType?: ResourceType;
  resourceAmount?: number;
}

// Tree regrowth stages
export enum RegrowthStage {
  None = 0,
  Seedling = 1,    // 0-0.5 days - tiny sprout
  Sapling = 2,     // 0.5-1 day - small tree
  YoungTree = 3,   // 1-1.5 days - medium tree
  Mature = 4       // 1.5-2 days - full tree, harvestable
}

export class Tile {
  readonly x: number;
  readonly y: number;
  type: TileType;
  walkable: boolean;
  buildable: boolean;
  resourceType: ResourceType | null;
  resourceAmount: number;
  structureId: string | null = null;

  // Regrowth tracking
  regrowthStage: RegrowthStage = RegrowthStage.None;
  regrowthTimer: number = 0;  // Time accumulated in current regrowth
  private wasTree: boolean = false;
  private wasBush: boolean = false;

  // Visual properties
  private modified: boolean = false;
  private originalType: TileType;

  constructor(x: number, y: number, config: TileConfig) {
    this.x = x;
    this.y = y;
    this.type = config.type;
    this.originalType = config.type;
    this.walkable = config.walkable;
    this.buildable = config.buildable;
    this.resourceType = config.resourceType ?? null;
    this.resourceAmount = config.resourceAmount ?? 0;
    this.wasTree = config.type === TileType.Tree;
    this.wasBush = config.type === TileType.Bush;
  }

  /** Check if tile has harvestable resources */
  hasResource(): boolean {
    return this.resourceType !== null && this.resourceAmount > 0;
  }

  /** Harvest resource from tile */
  harvest(amount: number): number {
    const harvested = Math.min(amount, this.resourceAmount);
    this.resourceAmount -= harvested;

    if (this.resourceAmount <= 0) {
      this.resourceAmount = 0;
      this.resourceType = null;
      // Convert to depleted version if applicable
      if (this.type === TileType.Tree) {
        this.type = TileType.Grass;
        this.walkable = true;
        this.buildable = true;
        this.modified = true;
        // Start regrowth process
        this.regrowthStage = RegrowthStage.Seedling;
        this.regrowthTimer = 0;
        this.wasTree = true;
      } else if (this.type === TileType.Bush) {
        this.type = TileType.Grass;
        this.walkable = true;
        this.buildable = true;
        this.modified = true;
        // Start bush regrowth (faster than trees)
        this.regrowthStage = RegrowthStage.Seedling;
        this.regrowthTimer = 0;
        this.wasBush = true;
      } else if (this.type === TileType.Rock) {
        this.type = TileType.Stone;
        this.walkable = true;
        this.buildable = true;
        this.modified = true;
      } else if (this.type === TileType.IronOre) {
        this.type = TileType.Stone;
        this.walkable = true;
        this.buildable = true;
        this.modified = true;
      }
    }

    return harvested;
  }

  /** Update tree/bush regrowth - called by World with deltaTime in game-days */
  updateRegrowth(deltaDays: number): void {
    // Don't regrow if there's a structure on this tile
    if (this.structureId !== null) {
      return;
    }

    // Only process if regrowing
    if (this.regrowthStage === RegrowthStage.None) {
      return;
    }

    if (!this.wasTree && !this.wasBush) {
      return;
    }

    // Time per stage: trees = 0.5 days (2 days total), bushes = 0.25 days (1 day total)
    const timePerStage = this.wasBush ? 0.25 : 0.5;
    this.regrowthTimer += deltaDays;

    // Check for stage advancement
    if (this.regrowthTimer >= timePerStage) {
      this.regrowthTimer -= timePerStage;
      this.regrowthStage++;

      // Check if fully grown
      if (this.regrowthStage > RegrowthStage.Mature) {
        if (this.wasBush) {
          this.becomeBush();
        } else {
          this.becomeTree();
        }
      }
    }
  }

  /** Convert back to a full tree */
  private becomeTree(): void {
    this.type = TileType.Tree;
    this.walkable = false;
    this.buildable = false;
    this.resourceType = ResourceType.Wood;
    this.resourceAmount = 2;
    this.regrowthStage = RegrowthStage.None;
    this.regrowthTimer = 0;
  }

  /** Convert back to a full bush */
  private becomeBush(): void {
    this.type = TileType.Bush;
    this.walkable = true;  // Bushes are walkable
    this.buildable = true;
    this.resourceType = ResourceType.Fiber;
    this.resourceAmount = 2;
    this.regrowthStage = RegrowthStage.None;
    this.regrowthTimer = 0;
  }

  /** Check if tile is regrowing (for rendering) */
  isRegrowing(): boolean {
    return this.regrowthStage !== RegrowthStage.None && (this.wasTree || this.wasBush);
  }

  /** Check if regrowing a bush (for rendering) */
  isRegrowingBush(): boolean {
    return this.regrowthStage !== RegrowthStage.None && this.wasBush;
  }

  /** Get wasTree flag for saving */
  getWasTree(): boolean {
    return this.wasTree;
  }

  /** Get wasBush flag for saving */
  getWasBush(): boolean {
    return this.wasBush;
  }

  /** Set wasTree flag (for loading) */
  setWasTree(value: boolean): void {
    this.wasTree = value;
  }

  /** Set wasBush flag (for loading) */
  setWasBush(value: boolean): void {
    this.wasBush = value;
  }

  /** Get regrowth height for rendering */
  getRegrowthHeight(): number {
    // Bushes are smaller than trees
    if (this.wasBush) {
      switch (this.regrowthStage) {
        case RegrowthStage.Seedling: return 2;
        case RegrowthStage.Sapling: return 4;
        case RegrowthStage.YoungTree: return 6;
        case RegrowthStage.Mature: return 8;
        default: return 0;
      }
    }
    // Trees
    switch (this.regrowthStage) {
      case RegrowthStage.Seedling: return 4;
      case RegrowthStage.Sapling: return 10;
      case RegrowthStage.YoungTree: return 18;
      case RegrowthStage.Mature: return 26;
      default: return 0;
    }
  }

  /** Get regrowth color for rendering */
  getRegrowthColor(): string {
    // Bushes have a more yellow-green color
    if (this.wasBush) {
      switch (this.regrowthStage) {
        case RegrowthStage.Seedling: return '#7a9a5a';  // Light yellow-green
        case RegrowthStage.Sapling: return '#6a8a4a';   // Medium
        case RegrowthStage.YoungTree: return '#5a7a3a'; // Darker
        case RegrowthStage.Mature: return '#4a6a2a';    // Full bush
        default: return '#5a8a4a';
      }
    }
    // Trees
    switch (this.regrowthStage) {
      case RegrowthStage.Seedling: return '#5a8c5a';  // Light green
      case RegrowthStage.Sapling: return '#4a7c4a';   // Medium green
      case RegrowthStage.YoungTree: return '#3a6c3a'; // Darker green
      case RegrowthStage.Mature: return '#2d5a3d';    // Full tree green
      default: return '#4a7c59';
    }
  }

  /** Check if tile is occupied by a structure */
  hasStructure(): boolean {
    return this.structureId !== null;
  }

  /** Set structure on this tile */
  setStructure(structureId: string | null): void {
    this.structureId = structureId;
    if (structureId) {
      this.walkable = false;
      this.buildable = false;
    }
  }

  /** Get tile color for rendering */
  getColor(): string {
    switch (this.type) {
      case TileType.Grass:
        return '#4a7c59';
      case TileType.Dirt:
        return '#8b7355';
      case TileType.Stone:
        return '#808080';
      case TileType.Water:
        return '#4a90a4';
      case TileType.Sand:
        return '#c2b280';
      case TileType.Tree:
        return '#2d5a3d';
      case TileType.Rock:
        return '#606060';
      case TileType.Bush:
        return '#4a6a2a';  // Dark yellow-green for bushes
      case TileType.IronOre:
        return '#8a6a4a';  // Rusty brown for iron ore
      default:
        return '#333333';
    }
  }

  /** Get height for rendering (blocks) */
  getHeight(): number {
    switch (this.type) {
      case TileType.Rock:
        return 16;
      case TileType.Tree:
        return 32;
      case TileType.Bush:
        return 10;  // Bushes are shorter than trees
      case TileType.IronOre:
        return 20;  // Slightly taller than regular rocks
      case TileType.Water:
        return -8;
      default:
        return 0;
    }
  }

  /** Check if tile was modified from original */
  isModified(): boolean {
    return this.modified || this.type !== this.originalType || this.isRegrowing();
  }

  /** Serialize for saving */
  toSaveData(): object | null {
    if (!this.isModified()) return null;
    return {
      x: this.x,
      y: this.y,
      type: this.type,
      resourceAmount: this.resourceAmount,
      structureId: this.structureId,
      regrowthStage: this.regrowthStage,
      regrowthTimer: this.regrowthTimer,
      wasTree: this.wasTree
    };
  }

  /** Create default tile configurations */
  static getDefaultConfig(type: TileType): TileConfig {
    switch (type) {
      case TileType.Grass:
        return { type, walkable: true, buildable: true };
      case TileType.Dirt:
        return { type, walkable: true, buildable: true };
      case TileType.Stone:
        return { type, walkable: true, buildable: true };
      case TileType.Water:
        return { type, walkable: false, buildable: false };
      case TileType.Sand:
        return { type, walkable: true, buildable: true };
      case TileType.Tree:
        return {
          type,
          walkable: false,
          buildable: false,
          resourceType: ResourceType.Wood,
          resourceAmount: 2
        };
      case TileType.Rock:
        return {
          type,
          walkable: false,
          buildable: false,
          resourceType: ResourceType.Stone,
          resourceAmount: 2
        };
      case TileType.Bush:
        return {
          type,
          walkable: true,  // Bushes are walkable
          buildable: true,
          resourceType: ResourceType.Fiber,
          resourceAmount: 2
        };
      case TileType.IronOre:
        return {
          type,
          walkable: false,
          buildable: false,
          resourceType: ResourceType.Iron,
          resourceAmount: 3  // More valuable, gives 3 iron ore
        };
      default:
        return { type, walkable: true, buildable: true };
    }
  }
}
